import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';

// Enhanced Login Component
export const EnhancedLoginForm = ({ onSubmit, onForgotPassword, loading = false, error = '' }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      username: '',
      password: '',
      rememberMe: false
    }
  });

  const onFormSubmit = (data) => {
    onSubmit(data);
  };

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1 text-center">
        <CardTitle className="text-2xl font-bold text-mg-green">
          {t('auth.loginTitle')}
        </CardTitle>
        <CardDescription>
          {t('auth.loginSubtitle')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="username">{t('auth.username')}</Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('username', { 
                  required: t('auth.usernameRequired'),
                  minLength: {
                    value: 3,
                    message: t('auth.usernameMinLength')
                  }
                })}
                id="username"
                type="text"
                placeholder={t('auth.usernamePlaceholder')}
                className="pl-10"
              />
            </div>
            {errors.username && (
              <p className="text-sm text-red-500">{errors.username.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('password', { 
                  required: t('auth.passwordRequired'),
                  minLength: {
                    value: 6,
                    message: t('auth.passwordMinLength')
                  }
                })}
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.passwordPlaceholder')}
                className="pl-10 pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <input
                {...register('rememberMe')}
                id="rememberMe"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-mg-green focus:ring-mg-green"
              />
              <Label htmlFor="rememberMe" className="text-sm">
                {t('auth.rememberMe')}
              </Label>
            </div>
            <Button
              type="button"
              variant="link"
              className="text-sm text-mg-green hover:text-mg-green/80 p-0"
              onClick={onForgotPassword}
            >
              {t('auth.forgotPassword')}
            </Button>
          </div>

          <Button
            type="submit"
            className="w-full bg-mg-green hover:bg-mg-green/90"
            disabled={loading || !isValid}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.signingIn')}
              </>
            ) : (
              t('auth.login')
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Enhanced Registration Component
export const EnhancedRegisterForm = ({ onSubmit, onBackToLogin, loading = false, error = '' }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      fullName: '',
      acceptTerms: false
    }
  });

  const password = watch('password');
  
  const onFormSubmit = (data) => {
    onSubmit(data);
  };

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword(prev => !prev);
  }, []);

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, text: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    const strengthLevels = [
      { text: t('auth.passwordVeryWeak'), color: 'bg-red-500' },
      { text: t('auth.passwordWeak'), color: 'bg-orange-500' },
      { text: t('auth.passwordFair'), color: 'bg-yellow-500' },
      { text: t('auth.passwordGood'), color: 'bg-blue-500' },
      { text: t('auth.passwordStrong'), color: 'bg-green-500' }
    ];

    return {
      strength,
      text: strengthLevels[strength - 1]?.text || '',
      color: strengthLevels[strength - 1]?.color || 'bg-gray-300'
    };
  };

  const passwordStrength = getPasswordStrength(password);

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1 text-center">
        <div className="flex items-center justify-center mb-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute left-4 top-4"
            onClick={onBackToLogin}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <CardTitle className="text-2xl font-bold text-mg-green">
          {t('auth.createAccount')}
        </CardTitle>
        <CardDescription>
          {t('auth.createAccountSubtitle')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="fullName">{t('auth.fullName')}</Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('fullName', { 
                  required: t('auth.fullNameRequired'),
                  minLength: {
                    value: 2,
                    message: t('auth.fullNameMinLength')
                  }
                })}
                id="fullName"
                type="text"
                placeholder={t('auth.fullNamePlaceholder')}
                className="pl-10"
              />
            </div>
            {errors.fullName && (
              <p className="text-sm text-red-500">{errors.fullName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="username">{t('auth.username')}</Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('username', { 
                  required: t('auth.usernameRequired'),
                  minLength: {
                    value: 3,
                    message: t('auth.usernameMinLength')
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_]+$/,
                    message: t('auth.usernamePattern')
                  }
                })}
                id="username"
                type="text"
                placeholder={t('auth.usernamePlaceholder')}
                className="pl-10"
              />
            </div>
            {errors.username && (
              <p className="text-sm text-red-500">{errors.username.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('email', { 
                  required: t('auth.emailRequired'),
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: t('auth.emailInvalid')
                  }
                })}
                id="email"
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                className="pl-10"
              />
            </div>
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.password')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('password', { 
                  required: t('auth.passwordRequired'),
                  minLength: {
                    value: 8,
                    message: t('auth.passwordMinLength')
                  },
                  validate: (value) => {
                    const strength = getPasswordStrength(value).strength;
                    return strength >= 3 || t('auth.passwordTooWeak');
                  }
                })}
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.passwordPlaceholder')}
                className="pl-10 pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            {password && (
              <div className="space-y-1">
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={`h-1 w-full rounded ${
                        level <= passwordStrength.strength
                          ? passwordStrength.color
                          : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {passwordStrength.text}
                </p>
              </div>
            )}
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('confirmPassword', { 
                  required: t('auth.confirmPasswordRequired'),
                  validate: (value) => {
                    return value === password || t('auth.passwordsDoNotMatch');
                  }
                })}
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                className="pl-10 pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={toggleConfirmPasswordVisibility}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="flex items-start space-x-2">
            <input
              {...register('acceptTerms', { 
                required: t('auth.termsRequired') 
              })}
              id="acceptTerms"
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-mg-green focus:ring-mg-green"
            />
            <Label htmlFor="acceptTerms" className="text-sm leading-5">
              {t('auth.acceptTerms')}
            </Label>
          </div>
          {errors.acceptTerms && (
            <p className="text-sm text-red-500">{errors.acceptTerms.message}</p>
          )}

          <Button
            type="submit"
            className="w-full bg-mg-green hover:bg-mg-green/90"
            disabled={loading || !isValid}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.creatingAccount')}
              </>
            ) : (
              t('auth.createAccount')
            )}
          </Button>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              {t('auth.alreadyHaveAccount')}{' '}
              <Button
                type="button"
                variant="link"
                className="text-mg-green hover:text-mg-green/80 p-0"
                onClick={onBackToLogin}
              >
                {t('auth.signIn')}
              </Button>
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Forgot Password Component
export const ForgotPasswordForm = ({ onSubmit, onBackToLogin, loading = false, error = '', success = false }) => {
  const { t } = useTranslation();
  
  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: ''
    }
  });

  const onFormSubmit = (data) => {
    onSubmit(data);
  };

  if (success) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
          <CardTitle className="text-2xl font-bold text-mg-green">
            {t('auth.emailSent')}
          </CardTitle>
          <CardDescription>
            {t('auth.emailSentDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={onBackToLogin}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('auth.backToLogin')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1 text-center">
        <div className="flex items-center justify-center mb-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute left-4 top-4"
            onClick={onBackToLogin}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <CardTitle className="text-2xl font-bold text-mg-green">
          {t('auth.forgotPassword')}
        </CardTitle>
        <CardDescription>
          {t('auth.forgotPasswordDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">{t('auth.email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('email', { 
                  required: t('auth.emailRequired'),
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: t('auth.emailInvalid')
                  }
                })}
                id="email"
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                className="pl-10"
              />
            </div>
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-mg-green hover:bg-mg-green/90"
            disabled={loading || !isValid}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.sendingEmail')}
              </>
            ) : (
              t('auth.sendResetEmail')
            )}
          </Button>

          <div className="text-center">
            <Button
              type="button"
              variant="link"
              className="text-sm text-mg-green hover:text-mg-green/80"
              onClick={onBackToLogin}
            >
              {t('auth.backToLogin')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Password Reset Component
export const PasswordResetForm = ({ onSubmit, loading = false, error = '', token = '' }) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });

  const password = watch('password');
  
  const onFormSubmit = (data) => {
    onSubmit({ ...data, token });
  };

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setShowConfirmPassword(prev => !prev);
  }, []);

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, text: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    const strengthLevels = [
      { text: t('auth.passwordVeryWeak'), color: 'bg-red-500' },
      { text: t('auth.passwordWeak'), color: 'bg-orange-500' },
      { text: t('auth.passwordFair'), color: 'bg-yellow-500' },
      { text: t('auth.passwordGood'), color: 'bg-blue-500' },
      { text: t('auth.passwordStrong'), color: 'bg-green-500' }
    ];

    return {
      strength,
      text: strengthLevels[strength - 1]?.text || '',
      color: strengthLevels[strength - 1]?.color || 'bg-gray-300'
    };
  };

  const passwordStrength = getPasswordStrength(password);

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1 text-center">
        <CardTitle className="text-2xl font-bold text-mg-green">
          {t('auth.resetPassword')}
        </CardTitle>
        <CardDescription>
          {t('auth.resetPasswordDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="password">{t('auth.newPassword')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('password', { 
                  required: t('auth.passwordRequired'),
                  minLength: {
                    value: 8,
                    message: t('auth.passwordMinLength')
                  },
                  validate: (value) => {
                    const strength = getPasswordStrength(value).strength;
                    return strength >= 3 || t('auth.passwordTooWeak');
                  }
                })}
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.passwordPlaceholder')}
                className="pl-10 pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            {password && (
              <div className="space-y-1">
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={`h-1 w-full rounded ${
                        level <= passwordStrength.strength
                          ? passwordStrength.color
                          : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {passwordStrength.text}
                </p>
              </div>
            )}
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                {...register('confirmPassword', { 
                  required: t('auth.confirmPasswordRequired'),
                  validate: (value) => {
                    return value === password || t('auth.passwordsDoNotMatch');
                  }
                })}
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                className="pl-10 pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={toggleConfirmPasswordVisibility}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-mg-green hover:bg-mg-green/90"
            disabled={loading || !isValid}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('auth.resettingPassword')}
              </>
            ) : (
              t('auth.resetPassword')
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Success Message Component
export const AuthSuccessMessage = ({ message, onContinue }) => {
  const { t } = useTranslation();
  
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardContent className="pt-6">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
          <h2 className="text-xl font-semibold text-mg-green">
            {t('auth.success')}
          </h2>
          <p className="text-muted-foreground">{message}</p>
          <Button
            onClick={onContinue}
            className="w-full bg-mg-green hover:bg-mg-green/90"
          >
            {t('auth.continue')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};